import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Surface, Btn } from "../components/Primitives";
import { ArrowsClockwise, Warning, Wrench, AirplaneTilt, Plus } from "@phosphor-icons/react";

const STATUS_COLOR = {
  overdue: { border: "border-l-red-500", badge: "bg-red-500/20 text-red-300 border-red-500/40", label: "OVERDUE" },
  urgent:  { border: "border-l-orange-500", badge: "bg-orange-500/20 text-orange-300 border-orange-500/40", label: "URGENT" },
  warning: { border: "border-l-amber",     badge: "bg-amber/15 text-amber border-amber/40",          label: "WARNING" },
  ok:      { border: "border-l-emerald-500", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "OK" },
};

const SEV_PILL = {
  expired:    "bg-red-500/20 text-red-300 border-red-500/40",
  urgent_7d:  "bg-orange-500/20 text-orange-300 border-orange-500/40",
  warning_30d:"bg-amber/15 text-amber border-amber/40",
};

export default function FleetMaintenance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/dashboard/maintenance-fleet");
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fleet = data?.fleet_ranked || [];

  return (
    <div data-testid="fleet-maintenance-page">
      <PageHeader
        overline="Operations"
        title="Fleet maintenance"
        subtitle="Every aircraft ranked by urgency. Hover a row to dig into the items, or click to open the aircraft profile."
        actions={
          <Btn variant="secondary" onClick={load} disabled={loading} data-testid="fleet-maintenance-refresh">
            <ArrowsClockwise size={14} className="inline mr-1.5" />
            {loading ? "Refreshing…" : "Refresh"}
          </Btn>
        }
      />
      <div className="p-8 space-y-6">
        {error && (
          <Surface className="p-4 border-red-500/40 bg-red-500/5">
            <div className="text-red-300 text-sm font-mono">{error}</div>
          </Surface>
        )}

        {/* Summary */}
        {data?.counts && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="fleet-summary">
            <SummaryTile label="Aircraft" value={data.counts.aircraft} accent="amber" />
            <SummaryTile label="Overdue" value={data.counts.overdue} accent={data.counts.overdue ? "red" : "zinc"} />
            <SummaryTile label="Due ≤30d" value={data.counts.due_30d} accent={data.counts.due_30d ? "orange" : "zinc"} />
            <SummaryTile label="Missing docs" value={data.counts.missing_docs} accent={data.counts.missing_docs ? "amber" : "zinc"} />
            <SummaryTile label="Open ADs" value={data.counts.open_ads} accent={data.counts.open_ads ? "amber" : "zinc"} />
          </div>
        )}

        {fleet.length === 0 && (
          <Surface className="p-12 text-center" data-testid="fleet-maintenance-empty">
            <AirplaneTilt size={36} className="text-zinc-700 mx-auto mb-3" />
            <div className="text-zinc-300 text-sm">No aircraft in this workspace yet.</div>
            <Btn onClick={() => navigate("/fleet")} className="mt-4" data-testid="add-aircraft-btn">
              <Plus size={14} className="inline mr-1.5" /> Add aircraft
            </Btn>
          </Surface>
        )}

        {fleet.length > 0 && (
          <div className="space-y-3">
            {fleet.map((ac) => (
              <FleetRow key={ac.id} ac={ac} onOpen={() => navigate(`/fleet/${ac.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent }) {
  const colorCls = {
    amber: "text-amber",
    red: "text-red-400",
    orange: "text-orange-400",
    zinc: "text-zinc-300",
  }[accent] || "text-zinc-300";
  return (
    <Surface className="p-4">
      <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1.5">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${colorCls}`}>{value ?? "—"}</div>
    </Surface>
  );
}

function FleetRow({ ac, onOpen }) {
  const cfg = STATUS_COLOR[ac.status] || STATUS_COLOR.ok;
  return (
    <Surface
      className={`p-5 border-l-4 ${cfg.border} hover:bg-zinc-950 cursor-pointer transition-colors`}
      onClick={onOpen}
      data-testid={`fleet-row-${ac.registration}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center shrink-0">
            <AirplaneTilt size={20} weight="duotone" className="text-amber" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-lg font-semibold tracking-tight">{ac.registration}</div>
              <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 border ${cfg.badge}`}>
                {cfg.label}
              </span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono">
              {ac.manufacturer} {ac.model}
              {ac.year_of_manufacture ? ` · ${ac.year_of_manufacture}` : ""}
              {ac.tach_time ? ` · ${ac.tach_time.toFixed(1)}h tach` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <CountChip label="Overdue" value={ac.counts.overdue} color={ac.counts.overdue ? "red" : "zinc"} />
          <CountChip label="Urgent" value={ac.counts.urgent} color={ac.counts.urgent ? "orange" : "zinc"} />
          <CountChip label="Warning" value={ac.counts.warning} color={ac.counts.warning ? "amber" : "zinc"} />
          <CountChip label="Missing docs" value={ac.counts.missing_docs} color={ac.counts.missing_docs ? "amber" : "zinc"} />
        </div>
      </div>

      {ac.items && ac.items.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-800 grid md:grid-cols-2 gap-3" data-testid={`fleet-items-${ac.registration}`}>
          {ac.items.map((it) => (
            <div key={`${it.kind}-${it.id}`} className="flex items-center gap-3 text-xs">
              <Wrench size={12} className="text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-zinc-200 truncate">{it.name}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{it.kind}{it.category ? ` · ${it.category}` : ""}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 border ${SEV_PILL[it.severity] || "border-zinc-800 text-zinc-400"}`}>
                {it.due_label}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-zinc-900 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber font-mono"
          data-testid={`fleet-open-${ac.registration}`}
        >
          Open profile →
        </button>
      </div>
    </Surface>
  );
}

function CountChip({ label, value, color }) {
  const colorCls = {
    red: "text-red-400",
    orange: "text-orange-400",
    amber: "text-amber",
    zinc: "text-zinc-500",
  }[color] || "text-zinc-400";
  return (
    <div className="text-center min-w-[60px]">
      <div className={`text-base font-semibold tabular-nums ${colorCls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-mono mt-0.5">{label}</div>
    </div>
  );
}
